import { Link } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  ShieldCheck,
  BarChart3,
  FileText,
  Layers,
  Clock,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const features = [
  { icon: Sparkles, title: "AI Question Generation", desc: "Generate MCQ, MSQ and subjective questions across difficulty levels with Claude." },
  { icon: ShieldCheck, title: "Secure Admin Access", desc: "Gated admin registration with a secret code and full student approval workflow." },
  { icon: Layers, title: "Personalized Papers", desc: "Unique question papers per student from a randomized pool to reduce cheating." },
  { icon: Clock, title: "Timed Examinations", desc: "Distraction-free exam runner with auto-submit and instant objective scoring." },
  { icon: BarChart3, title: "Deep Analytics", desc: "Difficulty accuracy, pass rates, question and AI analytics in one place." },
  { icon: FileText, title: "PDF Reports", desc: "Professional, downloadable result and examination reports for everyone." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border sticky top-0 bg-background/90 backdrop-blur z-40">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login-btn">Sign in</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-getstarted-btn" className="rounded-sm">Get started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 border border-border px-3 py-1 text-xs uppercase tracking-[0.2em] text-primary mb-6">
              <Sparkles className="h-3.5 w-3.5" /> AI-Powered Assessments
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              The examination platform built for modern institutions.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl">
              Scholarlio handles secure enrollment, AI question generation, controlled result
              publishing and enterprise analytics — end to end.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="rounded-sm" data-testid="hero-student-btn">
                  Enroll as Student <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/register/admin">
                <Button size="lg" variant="outline" className="rounded-sm" data-testid="hero-admin-btn">
                  Register as Admin
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["Mandatory registration numbers", "Approval workflow", "Subjective evaluation"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="border border-border bg-card shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2000"
                alt="Student taking exam"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-card border border-border p-4 shadow-sm hidden sm:block">
              <div className="overline">Avg. Pass Rate</div>
              <div className="font-display text-2xl font-bold text-primary">94.2%</div>
            </div>
          </div>
        </div>
      </section>

      {/* features */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="overline mb-2">Platform Capabilities</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
          Everything you need to run assessments at scale.
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-border">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="p-8 border-r border-b border-border hover:bg-secondary/50 transition-colors"
            >
              <f.icon className="h-6 w-6 text-primary" strokeWidth={1.6} />
              <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Ready to modernise your exams?</h2>
            <p className="mt-2 text-primary-foreground/80">Create your account in under a minute.</p>
          </div>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="rounded-sm" data-testid="cta-register-btn">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <Brand />
          <span>© {new Date().getFullYear()} Scholarlio</span>
        </div>
      </footer>
    </div>
  );
}
